import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { CheckCircle2, AlertCircle, Sparkles, ArrowRight } from "lucide-react";

const TIER = {
  excellent:        { c: "text-emerald-700 bg-emerald-50 border-emerald-200", l: "Excellent" },
  great:            { c: "text-emerald-700 bg-emerald-50 border-emerald-200", l: "Great" },
  good:             { c: "text-blue-700 bg-blue-50 border-blue-200", l: "Good" },
  getting_started:  { c: "text-amber-800 bg-amber-50 border-amber-200", l: "Getting started" },
  incomplete:       { c: "text-red-800 bg-red-50 border-red-200", l: "Incomplete" },
};

/**
 * Compact business readiness card shown on the dashboard.
 * Reads /api/business/readiness and shows score + top gaps.
 */
export default function BusinessReadinessCard() {
  const [data, setData] = useState(null);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/business/readiness");
        setData(data);
      } catch (_) { /* not available */ }
    })();
  }, []);

  if (!data) return null;
  const tier = TIER[data.tier] || TIER.good;

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5"
         data-testid="business-readiness-card">
      <div className="flex items-center justify-between">
        <h3 className="font-display text-lg font-semibold inline-flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-[#1B4332]" /> ConfirmPH setup progress
        </h3>
        <span className={`text-xs px-2 py-0.5 rounded-full border ${tier.c}`}
              data-testid="readiness-tier">{tier.l}</span>
      </div>
      <div className="mt-3 flex items-center gap-4">
        <div className="text-4xl font-display font-semibold text-slate-900"
             data-testid="readiness-score">{data.score}<span className="text-base text-slate-400">%</span></div>
        <div className="flex-1">
          <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
            <div className="h-full bg-[#1B4332]"
                 style={{ width: `${data.score}%` }} />
          </div>
          <div className="text-xs text-slate-500 mt-1">
            {data.done_count} of {data.total_count} items complete
          </div>
        </div>
      </div>

      {data.gaps?.length > 0 && (
        <div className="mt-3">
          <button onClick={() => setExpanded((v) => !v)}
                  data-testid="readiness-toggle-gaps"
                  className="text-xs text-[#1B4332] hover:underline inline-flex items-center gap-1">
            {expanded ? "Hide" : `Show ${data.gaps.length} thing${data.gaps.length === 1 ? "" : "s"} to improve`}
          </button>
          {expanded && (
            <ul className="mt-2 space-y-1.5" data-testid="readiness-gaps">
              {data.gaps.map((g) => (
                <li key={g.key} className="flex items-start gap-2 text-sm"
                    data-testid={`readiness-gap-${g.key}`}>
                  <AlertCircle className="w-3.5 h-3.5 mt-0.5 text-amber-600 flex-shrink-0" />
                  <div className="flex-1">
                    <div className="text-slate-700"><b>{g.label}</b>: {g.action}</div>
                  </div>
                  {g.action_path && (
                    <Link to={g.action_path}
                          data-testid={`readiness-gap-action-${g.key}`}
                          className="inline-flex items-center gap-1 text-xs text-[#1B4332] hover:underline whitespace-nowrap">
                      Fix it <ArrowRight className="w-3 h-3" />
                    </Link>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {data.gaps?.length === 0 && (
        <div className="mt-3 text-sm text-emerald-700 inline-flex items-center gap-1"
             data-testid="readiness-complete">
          <CheckCircle2 className="w-4 h-4" /> Everything looks great: your profile is complete.
        </div>
      )}
    </div>
  );
}
